import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { FiArrowLeft, FiSave, FiPlus, FiX } from "react-icons/fi";
import ImageUpload from "../components/ImageUpload";
import LoadingSpinner from "../components/LoadingSpinner";
import axios from "axios";

const ProjectForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEdit);
  const [images, setImages] = useState([]); // {file?, url, public_id, isNew, name}
  const [technologies, setTechnologies] = useState([]);
  const [newTech, setNewTech] = useState("");

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm({
    defaultValues: {
      title: "",
      description: "",
      liveUrl: "",
      githubUrl: "",
      featured: false,
    },
  });

  const API_URL = process.env.REACT_APP_API_URL;

  // Fetch project if editing
  useEffect(() => {
    if (isEdit) fetchProject();
  }, [id, isEdit]);

  const fetchProject = async () => {
    const token = localStorage.getItem("adminToken");
    if (!token) return navigate("/login");

    try {
      const response = await axios.get(`${API_URL}/projects/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const project = response.data.data;

      setValue("title", project.title);
      setValue("description", project.description);
      setValue("liveUrl", project.liveUrl || "");
      setValue("githubUrl", project.githubUrl || "");
      setValue("featured", project.featured);

      setTechnologies(project.technologies || []);
      setImages(
        (project.images || []).map((img) => ({
          url: img.url,
          public_id: img.public_id,
          isNew: false,
          name: img.alt || "Project Image",
        }))
      );
    } catch (error) {
      toast.error("Failed to fetch project");
      navigate("/projects");
    } finally {
      setInitialLoading(false);
    }
  };

  // Add technology
  const addTechnology = () => {
    const tech = String(newTech).trim(); // fix: ensure string before trim
    if (tech && !technologies.includes(tech)) {
      setTechnologies([...technologies, tech]);
      setNewTech("");
    }
  };

  const removeTechnology = (tech) => {
    setTechnologies(technologies.filter((t) => t !== tech));
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addTechnology();
    }
  };

  // Submit form
  const onSubmit = async (data) => {
    if (!data.title?.trim() || !data.description?.trim()) {
      return toast.error("Title & Description required");
    }

    if (technologies.length === 0)
      return toast.error("Add at least one technology");

    setLoading(true);
    const token = localStorage.getItem("adminToken");
    if (!token) {
      toast.error("You must be logged in as admin");
      setLoading(false);
      return;
    }

    try {
      const formData = new FormData();
      formData.append("title", data.title);
      formData.append("description", data.description);
      formData.append("liveUrl", data.liveUrl || "");
      formData.append("githubUrl", data.githubUrl || "");
      formData.append("featured", data.featured ? "true" : "false");
      formData.append("technologies", JSON.stringify(technologies));

      // Append new images
      images
        .filter((img) => img.isNew && img.file)
        .forEach((img) => {
          formData.append("projectImages", img.file);
        });

      // Append existing images for update
      if (isEdit) {
        const existingImages = images
          .filter((img) => !img.isNew)
          .map((img) => ({
            url: img.url,
            public_id: img.public_id,
            alt: img.name || data.title,
          }));
        formData.append("existingImages", JSON.stringify(existingImages));
      }

      const url = isEdit ? `${API_URL}/projects/${id}` : `${API_URL}/projects`;
      const method = isEdit ? axios.put : axios.post;

      await method(url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });

      toast.success(`Project ${isEdit ? "updated" : "created"} successfully`);
      navigate("/projects");
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Failed to save project");
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate("/projects")}
          className="btn btn-secondary !p-2"
        >
          <FiArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEdit ? "Edit Project" : "New Project"}
          </h1>
          <p className="text-gray-600">
            {isEdit
              ? "Update project details"
              : "Add a new project to your portfolio"}
          </p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Info */}
            <div className="card p-6">
              <h2 className="text-lg font-semibold mb-4">Basic Information</h2>
              <div className="space-y-4">
                <div>
                  <label className="form-label">Project Title *</label>
                  <input
                    type="text"
                    className={`form-input ${
                      errors.title ? "border-red-500" : ""
                    }`}
                    placeholder="Enter project title"
                    {...register("title", { required: "Title is required" })}
                  />
                  {errors.title && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.title.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="form-label">Description *</label>
                  <textarea
                    rows={4}
                    className={`form-input ${
                      errors.description ? "border-red-500" : ""
                    }`}
                    placeholder="Describe your project"
                    {...register("description", {
                      required: "Description is required",
                    })}
                  />
                  {errors.description && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.description.message}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">Live URL</label>
                    <input
                      type="url"
                      className="form-input"
                      placeholder="https://example.com"
                      {...register("liveUrl")}
                    />
                  </div>

                  <div>
                    <label className="form-label">GitHub URL</label>
                    <input
                      type="url"
                      className="form-input"
                      placeholder="https://github.com/username/repo"
                      {...register("githubUrl")}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Technologies */}
            <div className="card p-6">
              <h2 className="text-lg font-semibold mb-4">Technologies</h2>
              <div className="space-y-4">
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={newTech}
                    onChange={(e) => setNewTech(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className="form-input flex-1"
                    placeholder="Add technology (e.g., React, Node.js)"
                  />
                  <button
                    type="button"
                    onClick={addTechnology}
                    className="btn btn-primary !px-3"
                    disabled={!newTech.trim()}
                  >
                    <FiPlus size={16} />
                  </button>
                </div>

                {technologies.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {technologies.map((tech, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm"
                      >
                        {tech}
                        <button
                          type="button"
                          onClick={() => removeTechnology(tech)}
                          className="ml-2 text-blue-600 hover:text-blue-800"
                        >
                          <FiX size={14} />
                        </button>
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">
                    No technologies added yet
                  </p>
                )}
              </div>
            </div>

            {/* Images */}
            <div className="card p-6">
              <ImageUpload
                images={images}
                onImagesChange={setImages}
                maxFiles={5}
              />
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <div className="card p-6">
              <h2 className="text-lg font-semibold mb-4">Actions</h2>
              <div className="space-y-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="featured"
                    className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    {...register("featured")}
                  />
                  <label
                    htmlFor="featured"
                    className="ml-2 text-sm text-gray-700"
                  >
                    Featured Project
                  </label>
                </div>

                <div className="pt-4 border-t border-gray-200 space-y-3">
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <LoadingSpinner size="sm" className="mr-2" />
                    ) : (
                      <FiSave className="mr-2" size={16} />
                    )}
                    {loading
                      ? "Saving..."
                      : isEdit
                      ? "Update Project"
                      : "Create Project"}
                  </button>

                  <button
                    type="button"
                    onClick={() => navigate("/projects")}
                    className="w-full btn btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ProjectForm;
